import { IconDefinition } from "@fortawesome/fontawesome-svg-core";
import { faBell, faCheck, faQuestion, faXmark } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useEffect, useMemo, useState } from "react";
import { useShallow } from "zustand/react/shallow";
import { MoreToggle } from "../../components/MoreToggle";
import { cubeGeometry } from "../../state/cubeGeometry";
import { usePublications, type Publication } from "../../state/publications";
import { Cube } from "../../state/types";
import {
  formatDate,
  formatDateShort,
  formatDistance,
  formatDistanceShort,
  outcome,
  splitPolicyAndPermits,
  typeLabel,
  withoutAnsweredApplications,
  type Outcome,
} from "./publicationsFormat";
import { A, Info, SubHeader } from "./util";

const SHOW = 5;

const COLUMNS = 3;

const ICONS: Record<Outcome, { icon: IconDefinition; className: string }> = {
  pending: { icon: faQuestion, className: "text-gray-500" },
  positive: { icon: faCheck, className: "text-green" },
  negative: { icon: faXmark, className: "text-red-600" },
  // Een melding kent geen uitkomst: ze is gedaan, niet beoordeeld
  notification: { icon: faBell, className: "text-gray-500" },
};

/** Neemt altijd dezelfde breedte in, zodat de typekolom recht blijft. */
const StatusIcon = ({ status }: { status?: string }) => {
  const o = outcome(status);
  return (
    <span className="inline-block w-4" title={o ? status!.replace("_", " ") : undefined}>
      {o && <FontAwesomeIcon icon={ICONS[o].icon} className={`inline w-3 ${ICONS[o].className}`} />}
    </span>
  );
};

/** Eén regel in de tabel; een klik vouwt de hele bekendmaking eronder open. */
const Row = ({ p }: { p: Publication }) => {
  const [open, setOpen] = useState(false);
  return (
    <>
      <tr
        className={`cursor-pointer align-top hover:bg-bright-yellow/40 ${open ? "bg-bright-yellow/20" : ""}`}
        onClick={() => setOpen(!open)}
        aria-expanded={open}
      >
        <td className="px-2.5">{formatDateShort(p.gepubliceerdOp)}</td>
        <td className="px-1.5 text-right whitespace-nowrap">
          {formatDistanceShort(p.afstandMeter)}
        </td>
        <td className="px-2.5 truncate" title={typeLabel(p)}>
          <StatusIcon status={p.procedurestatus} />
          {typeLabel(p)}
        </td>
      </tr>
      {open && (
        <tr className="bg-bright-yellow/20">
          <td colSpan={COLUMNS} className="px-2.5 pb-2 break-words">
            <A href={p.url}>{p.titel}</A>
            <div className="text-gray-600">
              {[
                p.geometrielabel,
                p.blad,
                formatDate(p.gepubliceerdOp),
                formatDistance(p.afstandMeter),
              ]
                .filter(Boolean)
                .join(" - ")}
            </div>
          </td>
        </tr>
      )}
    </>
  );
};

/**
 * Eén lijst binnen de tabel: een gestippelde scheidingslijn met rechts wat
 * voor lijst het is, dan de regels. Een eigen `<tbody>`, zodat alle lijsten
 * dezelfde kolommen en dezelfde kop delen.
 */
const Group = ({ label, rows }: { label: string; rows: Publication[] }) => {
  const [more, setMore] = useState(false);
  if (!rows.length) return null;
  return (
    <tbody>
      <tr>
        <td colSpan={COLUMNS} className="px-2.5">
          <div className="flex items-center gap-2 text-xs text-gray-600">
            <span className="flex-1 border-t border-dashed border-gray" />
            {label} ({rows.length})
          </div>
        </td>
      </tr>
      {(more ? rows : rows.slice(0, SHOW)).map((p) => (
        <Row key={p.publicatieId} p={p} />
      ))}
      {rows.length > SHOW && (
        <tr>
          <td colSpan={COLUMNS} className="px-2.5 py-1">
            <MoreToggle open={more} setOpen={setMore} />
          </td>
        </tr>
      )}
    </tbody>
  );
};

/**
 * Officiële bekendmakingen rond dit perceel, uit het publicatieregister van de
 * omgevingsmonitor. De straal ligt om de geometrie van de cube, dus dezelfde
 * sectie werkt op /location, in een projectrapport (daar is de cube de unie
 * van alle percelen) en in de kaveldrawer van de workspace.
 *
 * Bovenaan wat het perceel zelf raakt, vijf jaar terug en nieuwste eerst: een
 * vergunning van vier jaar geleden óp het perceel zegt meer dan een van
 * gisteren op 500 meter. Daaronder de buurt, in twee lijsten, want dat zijn twee
 * soorten nieuws. Beleid — een omgevingsvisie, een verordening — zegt wat er
 * mag gaan gelden; een vergunning zegt wat er werkelijk gebeurt, meestal op het
 * perceel ernaast. Door elkaar gezet verliest het beleid het altijd: dat hangt
 * aan een gemeente en de vergunning aan een adres, dus op afstand staat de
 * dakkapel bovenaan.
 */
export const Publications = ({ cube }: { cube: Cube }) => {
  const geometry = useMemo(() => cube?.geometry && cubeGeometry(cube), [cube]);
  const [state, publications, load] = usePublications(
    useShallow((s) => [s.state, s.publications, s.load])
  );

  useEffect(() => {
    if (geometry) load(geometry);
  }, [geometry, load]);

  // Een aanvraag die op dezelfde dag als zijn besluit verscheen, zegt niets
  // meer dat het besluit niet zegt
  const onParcel = useMemo(
    () => withoutAnsweredApplications(publications?.onParcel ?? []),
    [publications]
  );
  const nearby = useMemo(
    () => withoutAnsweredApplications(publications?.nearby ?? []),
    [publications]
  );

  // Een storing bij de monitor of eenvoudigweg niets in de buurt: in beide
  // gevallen is een lege sectie eerlijker dan een foutmelding over een bron
  // die de lezer niet kent.
  if (state === "error" || !(onParcel.length || nearby.length)) return null;

  const { policy, permits } = splitPolicyAndPermits(nearby);
  return (
    <>
      <SubHeader>Bekendmakingen</SubHeader>
      <Info divide={false}>
        {/* Vaste kolommen: het rapport is maar ~400px breed, dus een lange soort
            kapt af in plaats van de tabel voorbij de rand te rekken */}
        <table className="w-full table-fixed my-1">
          <thead className="text-left text-gray-600">
            <tr>
              <th className="w-28 px-2.5 font-normal">Datum</th>
              <th className="w-20 px-1.5 font-normal text-right">Afstand</th>
              <th className="px-2.5 font-normal">Type</th>
            </tr>
          </thead>
          <Group label="op het perceel" rows={onParcel} />
          <Group label="beleid en plannen in de buurt" rows={policy} />
          <Group label="vergunningen en meldingen in de buurt" rows={permits} />
        </table>
      </Info>
    </>
  );
};
